// src/lib/LogoCropModal.js
// Set a company logo from a FILE or a pasted LINK: drag to center, zoom with
// the slider (or scroll/pinch), the squircle frame IS the crop, and the saved
// result is always a ≤150px WebP data-URL a few KB in size.
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { X, Upload, Link2 } from 'lucide-react';
import { API_BASE } from '../apiBase';

const FRAME = 240;  // on-screen crop frame (px)
const OUT = 150;    // final logo size (px)

export default function LogoCropModal({ onSave, onClose }) {
  const [imgSrc, setImgSrc] = useState(null);
  const [nat, setNat] = useState(null);       // { w, h }
  const [zoom, setZoom] = useState(1);        // 1 = cover the frame
  // Letterbox fill behind zoomed-out logos. The eyedropper (Chrome's
  // EyeDropper API) samples any pixel on screen — e.g. the logo's own
  // background — for a seamless fill.
  const [fill, setFill] = useState('#ffffff');
  const eyedrop = async () => {
    try {
      if (!window.EyeDropper) { alert('Eyedropper needs Chrome/Edge — use the color box instead.'); return; }
      const r = await new window.EyeDropper().open();
      if (r && r.sRGBHex) setFill(r.sRGBHex);
    } catch (_) { /* user pressed Esc */ }
  };
  const [pos, setPos] = useState({ x: 0, y: 0 }); // pan offset in screen px
  const [linkVal, setLinkVal] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const imgRef = useRef(null);
  const dragRef = useRef(null);

  const loadSrc = (src) => {
    const im = new Image();
    im.onload = () => { setNat({ w: im.naturalWidth, h: im.naturalHeight }); setImgSrc(src); setZoom(1); setPos({ x: 0, y: 0 }); setErr(''); };
    im.onerror = () => setErr('Could not read that image.');
    im.src = src;
  };

  const onFile = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => loadSrc(reader.result);
    reader.readAsDataURL(f);
  };

  const fetchLink = async () => {
    if (!linkVal.trim()) return;
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${API_BASE}/relationships/fetch-image`, { url: linkVal.trim() },
        { headers: { Authorization: localStorage.getItem('token') } });
      loadSrc(r.data.dataUrl);
    } catch (e) {
      setErr(e.response?.data?.error || 'Could not fetch that link.');
    } finally { setBusy(false); }
  };

  // cover-fit base scale: at zoom 1 the image exactly fills the frame.
  // minZoom lets wide/tall logos zoom OUT to fully fit (letterboxed white).
  const base = nat ? FRAME / Math.min(nat.w, nat.h) : 1;
  const minZoom = nat ? Math.min(nat.w, nat.h) / Math.max(nat.w, nat.h) : 1;
  const dispW = nat ? nat.w * base * zoom : 0;
  const dispH = nat ? nat.h * base * zoom : 0;
  // clamp pan so the frame never shows past the image edge
  const clamp = (p, z = zoom) => {
    const dw = nat.w * base * z, dh = nat.h * base * z;
    const maxX = Math.max((dw - FRAME) / 2, 0), maxY = Math.max((dh - FRAME) / 2, 0);
    return { x: Math.min(Math.max(p.x, -maxX), maxX), y: Math.min(Math.max(p.y, -maxY), maxY) };
  };

  const startDrag = (cx, cy) => { dragRef.current = { sx: cx, sy: cy, ox: pos.x, oy: pos.y }; };
  const moveDrag = (cx, cy) => {
    const d = dragRef.current;
    if (!d) return;
    setPos(clamp({ x: d.ox + (cx - d.sx), y: d.oy + (cy - d.sy) }));
  };
  const endDrag = () => { dragRef.current = null; };

  const setZoomClamped = (z) => {
    const nz = Math.min(Math.max(z, minZoom), 6);
    setZoom(nz);
    setPos(p => clamp(p, nz));
  };

  useEffect(() => {
    const up = () => endDrag();
    window.addEventListener('mouseup', up);
    window.addEventListener('touchend', up);
    return () => { window.removeEventListener('mouseup', up); window.removeEventListener('touchend', up); };
  }, []);

  const save = () => {
    if (!imgSrc || !nat) return;
    // The frame shows a FRAME×FRAME window centered at (center - pos) of the
    // displayed image — map that window back to source pixels.
    const canvas = document.createElement('canvas');
    canvas.width = OUT; canvas.height = OUT;
    const ctx = canvas.getContext('2d');
    const im = imgRef.current;
    const k = OUT / FRAME;
    ctx.fillStyle = fill;
    ctx.fillRect(0, 0, OUT, OUT); // letterbox fill behind contained logos
    ctx.drawImage(im, ((FRAME - dispW) / 2 + pos.x) * k, ((FRAME - dispH) / 2 + pos.y) * k, dispW * k, dispH * k);
    let out;
    try { out = canvas.toDataURL('image/webp', 0.85); } catch (_) { out = canvas.toDataURL('image/jpeg', 0.85); }
    if (out.length < 200) out = canvas.toDataURL('image/jpeg', 0.85); // webp unsupported fallback
    onSave(out);
  };

  return createPortal(
    <div className="fixed inset-0 z-[90] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 rounded-2xl border border-gray-800 w-full max-w-md p-6 max-md:p-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">Company Logo</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-800 text-zinc-400"><X className="w-5 h-5" /></button>
        </div>

        {!imgSrc ? (
          <div className="space-y-3">
            <label className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-700 hover:border-gray-500 bg-gray-800/40 px-4 py-8 cursor-pointer text-sm text-zinc-400 font-medium">
              <Upload className="w-4 h-4" /> Upload an image
              <input type="file" accept="image/*" className="hidden" onChange={onFile} />
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Link2 className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={linkVal}
                  onChange={e => setLinkVal(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') fetchLink(); }}
                  placeholder="…or paste an image link"
                  className="w-full pl-9 pr-3 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-indigo-600/60"
                />
              </div>
              <button onClick={fetchLink} disabled={busy} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 text-white rounded-xl text-sm font-semibold">
                {busy ? '…' : 'Fetch'}
              </button>
            </div>
            {err && <p className="text-red-400 text-sm">{err}</p>}
          </div>
        ) : (
          <div>
            {/* the squircle frame IS the crop — drag to center, zoom to fit */}
            <div className="flex justify-center">
              <div
                className="relative overflow-hidden rounded-[28%] border-2 border-indigo-500/60 cursor-grab active:cursor-grabbing touch-none select-none"
                style={{ width: FRAME, height: FRAME, background: fill }}
                onMouseDown={e => startDrag(e.clientX, e.clientY)}
                onMouseMove={e => moveDrag(e.clientX, e.clientY)}
                onTouchStart={e => startDrag(e.touches[0].clientX, e.touches[0].clientY)}
                onTouchMove={e => moveDrag(e.touches[0].clientX, e.touches[0].clientY)}
                onWheel={e => { e.preventDefault(); setZoomClamped(zoom + (e.deltaY > 0 ? -0.08 : 0.08)); }}
              >
                <img
                  ref={imgRef}
                  src={imgSrc}
                  alt=""
                  draggable={false}
                  className="absolute pointer-events-none max-w-none"
                  style={{
                    width: dispW, height: dispH,
                    left: (FRAME - dispW) / 2 + pos.x,
                    top: (FRAME - dispH) / 2 + pos.y,
                  }}
                />
              </div>
            </div>
            <div className="flex items-center gap-3 mt-4">
              <span className="text-[11px] text-zinc-500 font-bold">−</span>
              <input type="range" min={minZoom} max="6" step="0.01" value={zoom}
                onChange={e => setZoomClamped(Number(e.target.value))}
                className="flex-1 accent-indigo-500" />
              <span className="text-sm text-zinc-500 font-bold">+</span>
            </div>
            <div className="flex items-center justify-center gap-2 mt-3">
              <span className="text-[11px] text-zinc-500 font-bold uppercase tracking-wider">Fill</span>
              {['#ffffff', '#000000'].map(c => (
                <button key={c} onClick={() => setFill(c)} title={c === '#ffffff' ? 'White' : 'Black'}
                  className={`w-6 h-6 rounded-md border-2 ${fill === c ? 'border-indigo-400' : 'border-gray-700'}`}
                  style={{ background: c }} />
              ))}
              <input type="color" value={fill} onChange={e => setFill(e.target.value)} title="Custom color"
                className="w-8 h-6 bg-transparent border border-gray-700 rounded-md cursor-pointer p-0" />
              <button onClick={eyedrop} title="Eyedropper — pick any color on screen (e.g. the logo's own background)"
                className="px-2 py-1 rounded-md bg-gray-800 hover:bg-gray-700 text-xs text-zinc-300">💧 Pick</button>
            </div>
            <p className="text-[11px] text-zinc-500 mt-1 text-center">Drag to center · zoom to fit — what's in the frame is the logo (saved at 150px)</p>
            <div className="flex justify-between gap-3 mt-4">
              <button onClick={() => { setImgSrc(null); setNat(null); setLinkVal(''); }} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-xl text-sm font-semibold text-zinc-300">
                Different image
              </button>
              <div className="flex gap-2">
                <button onClick={onClose} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-xl text-sm font-semibold text-zinc-300">Cancel</button>
                <button onClick={save} className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-sm font-bold">Set Logo</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
